import { ASSISTANCE_USES, CALIFORNIA_COUNTIES } from 'components/global/Constant/constants';
import { AppFormInputValue, AppFormSelectValue } from 'components/uikit';
import api from 'api/api';
import $userAccountSignal from 'signals/UserAccount.signal';
import $loaderSignal from 'signals/Loader.signal';
import $alertSignal from 'signals/Alert.signal';
import history from '../../../../history'

export type QuickMatchingForm = {
  loanAmount: AppFormInputValue;
  loanPurpose: AppFormSelectValue;
  numberOfFullTimeEmployees: AppFormInputValue;
  previousAnnualRevenue: AppFormInputValue;
  yearBusinessOpened: AppFormInputValue;
  county: AppFormSelectValue,
}

export type QuickMatchingFormTouched = {
  [K in keyof QuickMatchingForm]?: boolean
}

export type QuickMatchingFormErrors = {
  [K in keyof QuickMatchingForm]?: string[]
}

export async function initForm(): Promise<QuickMatchingForm | undefined> {
  try {
    $loaderSignal.update({ isContentLoading: true });

    const business = await api.get({
      path: '/businesses',
      options: {},
    });

    return {
      county: business.county.toString() || '',
      yearBusinessOpened: business.yearBusinessOpened?.toString() || '',
      loanAmount: business.assistance?.loanAmount?.toString() || '',
      loanPurpose: business.assistance?.loanPurpose?.toString() || '',
      numberOfFullTimeEmployees: business.employment?.numberOfFullTimeEmployees?.toString() || '',
      previousAnnualRevenue: business.finance?.previousAnnualRevenue?.toString() || '',
    }
  } catch (error) {
    $alertSignal.update({
      type: 'notification',
      error,
      message: error instanceof Error ? error.message : 'An error occurred while fetching the business information.',
    });
  } finally {
    $loaderSignal.update({ isContentLoading: false });
  }
}

export function validateForm(form: QuickMatchingForm): QuickMatchingFormErrors  {
  const errors: QuickMatchingFormErrors = {};

  if (Number(form.loanAmount) <= 0) {
    errors.loanAmount = ['Loan amount must be positive.'];
  }

  if (Number(form.numberOfFullTimeEmployees) < 0) {
    errors.numberOfFullTimeEmployees = ['Number of employees must be greater than or equal to 0.'];
  }

  if (Number(form.yearBusinessOpened) < 1900 || Number(form.yearBusinessOpened) > new Date().getFullYear()) {
    errors.yearBusinessOpened = ['Must be a valid year.'];
  }

  if (Number(form.previousAnnualRevenue) < 0) {
    errors.previousAnnualRevenue = ['Previous annual revenue must be greater than or equal to 0.'];
  }

  if (!Object.values(ASSISTANCE_USES).map(u => u.value).includes(form.loanPurpose)) {
    errors.loanPurpose = ['Loan purpose is not valid.'];
  }

  if (!CALIFORNIA_COUNTIES.includes(form.county)) {
    errors.county = ['Must select a value.']
  }

  return errors;
}

export async function handleSubmission(form: QuickMatchingForm): Promise<void> {
  try {
    $loaderSignal.update({ isContentLoading: true });

    await api.patch({
      path: '/businesses',
      body: {
        where: {
          id: $userAccountSignal.value?.userData?.account?.business?.id,
        },
        data: {
          county: form.county,
          yearBusinessOpened: Number(form.yearBusinessOpened),
          employment: {
            numberOfFullTimeEmployees: Number(form.numberOfFullTimeEmployees),
          },
          assistance: {
            loanAmount: Number(form.loanAmount.replaceAll(',', '')),
            loanPurpose: form.loanPurpose,
          },
          finance: {
            previousAnnualRevenue: Number(form.previousAnnualRevenue.replaceAll(',', '')),
          },
        }
      },
    });

    const userData = await api.get({ 
      path: '/users/current',
      options: undefined,
    }, 3)

    $userAccountSignal.update({ userData });

    history.push('/business/lenders');
  } catch (error) {
    $alertSignal.update({
      type: 'notification',
      error,
      message: error instanceof Error ? error.message : 'An unknown error occurred while updating business information.',
    });
  } finally {
    $loaderSignal.update({ isContentLoading: false });
  }
}
